/**
 * App Root Component
 * Wraps the entire application with providers, error boundaries, and router
 */

import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';
import { AppProvider } from './stores/useAppStore';
import { TabProvider } from './stores/useTabStore';
import { I18nProvider } from './i18n/I18nProvider';
import { PreferencesProvider } from './context/PreferencesContext';
import { useThemeProvider } from './hooks';
import { AppShell } from './features/shell/AppShell';
import { RouteSync } from './features/shell/RouteSync';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { AuthProvider } from './auth/AuthProvider';
import { AuthGuard } from './auth/AuthGuard';
import { ComponentCatalog } from './features/component-catalog/ComponentCatalog';
import { kciEventBus } from './eventBus/KCiEventBus';
import { KCI_APP_IDS } from './eventBus/appIds';
import 'kci-design-lib/styles/global.scss';
import 'kci-design-lib/styles/tokens.css';
import 'kci-design-lib/styles/components.css';
import './styles/global.css';
import './styles/workspace-assets.css';
import './styles/file-viewer-tab.css';
// Syncfusion EJ2 PDF Viewer styles
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-buttons/styles/material.css';
import '@syncfusion/ej2-inputs/styles/material.css';
import '@syncfusion/ej2-popups/styles/material.css';
import '@syncfusion/ej2-lists/styles/material.css';
import '@syncfusion/ej2-navigations/styles/material.css';
import '@syncfusion/ej2-splitbuttons/styles/material.css';
import '@syncfusion/ej2-dropdowns/styles/material.css';
import '@syncfusion/ej2-pdfviewer/styles/material.css';
import '@syncfusion/ej2-spreadsheet/styles/material.css';
import '@syncfusion/ej2-react-image-editor/styles/material.css';

function isForShell(target: string | string[] | undefined): boolean {
  if (!target) {
    return true;
  }

  if (Array.isArray(target)) {
    return target.includes(KCI_APP_IDS.SHELL);
  }

  return target === KCI_APP_IDS.SHELL;
}

/**
 * Inner content component that needs theme provider
 * Must be inside AppProvider to access preferences context
 */
function AppInnerContent() {
  useEffect(() => {
    const unsubscribeClaraAck = kciEventBus.subscribe(
      'clara.contextPanel.opened',
      (event) => {
        if (!isForShell(event.target)) {
          return;
        }

        console.info(
          '[KCI-EventDebug] Received bus ack clara.contextPanel.opened:',
          event,
        );
      },
    );

    const onClaraAckWindowEvent = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail;
      console.info('[KCI-EventDebug] Received window ack clara-context-panel-opened:', detail);
    };

    window.addEventListener('clara-context-panel-opened', onClaraAckWindowEvent);

    return () => {
      unsubscribeClaraAck();
      window.removeEventListener('clara-context-panel-opened', onClaraAckWindowEvent);
    };
  }, []);

  // Apply theme to document based on preferences (now inside AppProvider context)
  useThemeProvider();

  return (
    <BrowserRouter>
      <AuthProvider>
        {/*
         * RouteSync hydrates the store from the URL and pushes store
         * mutations back to the URL. The /demo gallery sits outside
         * that store-driven contract (it's a preview surface that
         * shouldn't change app state), so RouteSync skips /demo
         * paths and the Routes table below renders the demo
         * components directly.
         */}
        <RouteSync />
        <Routes>
          <Route path="/ui-template" element={<ComponentCatalog />} />
          <Route path="/ui-template/:slug" element={<ComponentCatalog />} />
          <Route path="*" element={
            <AuthGuard>
              <AppShell />
            </AuthGuard>
          } />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ErrorBoundary level="root" componentName="App">
      <I18nProvider>
        <PreferencesProvider>
          <AppProvider>
            <TabProvider>
              <AppInnerContent />
            </TabProvider>
          </AppProvider>
        </PreferencesProvider>
      </I18nProvider>
    </ErrorBoundary>
  );
}

export default App;